import { Icon, Typography, Input, toast } from "top-brains-ui";
import styles from "@src/pages/login/register/RegisterAccount.module.scss";
import { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  ERROR_MSGS_CONSTANTS,
  LOGIN_PAGE_CONSTANTS,
  PASSWORD_VALIDATION,
  VALIDATION_CONSTANTS,
} from "@src/constants/UiConstants";
import { useNavigate } from "react-router-dom";
import {
  COMMON_COLORS,
  ICON_COLOR_CONSTANTS,
} from "@src/styles/ColorConstants";
import {
  useLazyGetAlreadyExistingUsersQuery,
  useRegisterMutation,
  useUpdateStudentProfileMutation,
} from "@src/api/LoginApiSlice";
import { setStudentDetails, setUserDetails } from "@src/app/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/app/store";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import CommonLoader from "@src/components/loader/CommonLoader";

export enum GenderEnum {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHERS = "OTHERS",
}

interface RegisterInitialValues {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  organization?: string;
  gender?: GenderEnum;
  superSetId?: string;
}

const RegisterAccount: React.FC<{
  isEdit?: boolean;
  editData?: any;
  onClose?: () => void;
}> = ({ isEdit, editData, onClose }) => {
  const navigate = useNavigate();
  const [registerUser, { isLoading }] = useRegisterMutation();
  const [updateUser] = useUpdateStudentProfileMutation();
  const [triggerUserExistsQuery] = useLazyGetAlreadyExistingUsersQuery();
  const dispatch = useDispatch();
  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails
  );
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [isValidLength, setIsValidLength] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordValidations, setShowPasswordValidations] = useState(false);
  const [emailExistsError, setEmailExistsError] = useState<string | null>(null);

  const registerInitialValues: RegisterInitialValues = {
    name: isEdit && editData?.name ? editData.name : "",
    email: isEdit && editData?.email ? editData.email : "",
    phone:
      isEdit && editData?.phoneNumber
        ? editData?.phoneNumber?.startsWith("+91")
          ? editData.phoneNumber
          : "+" + editData.phoneNumber
        : "+91-",
    password: "",
    confirmPassword: "",
    gender: isEdit && editData?.gender ? editData.gender : undefined,
    superSetId:
      isEdit && editData?.superSetId ? editData.superSetId : undefined,
  };

  const createValidationSchema = Yup.object({
    name: Yup.string()
      .min(3, ERROR_MSGS_CONSTANTS.NAME_MIN_ERR_MSG)
      .max(50, ERROR_MSGS_CONSTANTS.NAME_MAX_ERR_MSG)
      .matches(/^\S.*$/, "Name should not start with a space.")
      .matches(/^[^0-9]*$/, "Name should not contain numbers.")
      .matches(/^[a-zA-Z ]*$/, "Name should not contain special characters.")
      .required(ERROR_MSGS_CONSTANTS.NAME_REQUIRED),
    email: Yup.string()
      .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
      .test("dot-in-domain", "Invalid Email", (value) => {
        if (!value) return false;
        const parts = value.split("@");
        return parts.length === 2 && parts[1].includes(".");
      })
      .required(VALIDATION_CONSTANTS.EMAIL_REQUIRED),
    phone: Yup.string()
      .test("required", "Phone number is required", (value) => {
        return value !== "+91-";
      })
      .matches(/^\+91-\d{10}$/, "Phone number is not valid"),
    password: Yup.string()
      .min(8, VALIDATION_CONSTANTS.PASSWORD_MIN_LENGTH)
      .test(
        "password-requirements",
        "Password does not meet all requirements",
        (value) => {
          const hasLower = /[a-z]/.test(value || "");
          const hasUpper = /[A-Z]/.test(value || "");
          const hasNumber = /\d/.test(value || "");
          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value || "");
          return hasLower && hasUpper && hasNumber && hasSpecial;
        },
      )
      .required(VALIDATION_CONSTANTS.PASSWORD_REQUIRED),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], VALIDATION_CONSTANTS.PASSWORDS_MUST_MATCH)
      .required(VALIDATION_CONSTANTS.CONFIRM_PASSWORD_REQUIRED),
    gender: Yup.string()
      .required(VALIDATION_CONSTANTS.GENDER_REQUIRED)
      .oneOf(Object.values(GenderEnum)),
  });

  const editValidationSchema = Yup.object({
    name: Yup.string()
      .min(3, ERROR_MSGS_CONSTANTS.NAME_MIN_ERR_MSG)
      .max(50, ERROR_MSGS_CONSTANTS.NAME_MAX_ERR_MSG)
      .matches(/^\S.*$/, "Name should not start with a space.")
      .matches(/^[^0-9]*$/, "Name should not contain numbers.")
      .matches(/^[a-zA-Z ]*$/, "Name should not contain special characters.")
      .required(ERROR_MSGS_CONSTANTS.NAME_REQUIRED),

    phone: Yup.string()
      .test("required", "Phone number is required", (value) => {
        return value !== "+91-";
      })
      .matches(/^\+91-\d{10}$/, "Phone number is not valid"),

    gender: Yup.string()
      .required(VALIDATION_CONSTANTS.GENDER_REQUIRED)
      .oneOf(Object.values(GenderEnum)),
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setHasLowercase(/[a-z]/.test(password));
    setHasUppercase(/[A-Z]/.test(password));
    setHasNumber(/\d/.test(password));
    setHasSpecialChar(/[!@#$%^&*(),.?":{}|<>]/.test(password));
    setIsValidLength(password.length >= 8 && password.length <= 20);
  };

  const handleSubmit = async (values: RegisterInitialValues) => {
    try {
      if (isEdit && editData) {
        const updatedPayload: any = {};

        if (values.name !== editData.name) {
          updatedPayload.name = values.name;
        }

        if (values.phone !== editData.phoneNumber) {
          updatedPayload.phoneNumber = values.phone;
        }

        if (values.gender !== editData.gender) {
          updatedPayload.gender = values.gender;
        }
        if (values.superSetId !== editData.superSetId) {
          updatedPayload.superSetId = values.superSetId;
        }
        if (Object.keys(updatedPayload).length === 0) {
          toast.info("No changes detected");
          return;
        }

        const response = await updateUser({
          studentId: editData.id,
          payload: updatedPayload,
        }).unwrap();

        if (userDetails) {
          dispatch(
            setUserDetails({
              ...userDetails,
              ...updatedPayload,
            })
          );
        }

        onClose?.();
        toast.success("Success", response?.response);
      } else {
        const payload = {
          name: values.name,
          email: values.email?.toLowerCase(),
          phoneNumber: values.phone,
          password: values.password,
          privileges: ["STUDENT"],
          gender: values.gender,
        };

        const response = await registerUser({ payload }).unwrap();

        dispatch(setStudentDetails(response.response));
        navigate("/registration-success");
      }
    } catch (err) {
      const errorMessage =
        (err as { data?: { response?: string } })?.data?.response ??
        "Something went wrong";
      toast.error(errorMessage);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.logicLeftCard}>
        <div className={styles.textContainer}>
          <div className={styles.headingContainer}>
            <div>
              <Typography
                fontWeight="semi-bold"
                textAlign="center"
                fontSize={24}
                color="var(--modal-heading-color)"
              >
                {isEdit
                  ? "Edit Profile"
                  : LOGIN_PAGE_CONSTANTS.CREATE_ACCOUNT_HEADING}
              </Typography>
            </div>
            <div>
              <Typography
                fontWeight="medium"
                textAlign="center"
                fontSize={12}
                color="var(--modal-sub-heading-color)"
              >
                {LOGIN_PAGE_CONSTANTS.CREATE_ACCOUNT_SUB_HEADING}
              </Typography>
            </div>
          </div>
          <div className={styles.loginFormContainer}>
            <Formik
              initialValues={registerInitialValues}
              validationSchema={
                isEdit ? editValidationSchema : createValidationSchema
              }
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                isValid,
                dirty,
              }) => (
                <Form autoComplete="off">
                  <div className={styles.formFields}>
                    <div className={styles.formErrorField}>
                      <Field
                        as={Input}
                        name="name"
                        label={LOGIN_PAGE_CONSTANTS.NAME}
                        required
                        type="text"
                        placeholder={LOGIN_PAGE_CONSTANTS.NAME_PLACEHOLDER}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onFocus={() => {
                          setShowPasswordValidations(false);
                          setIsPasswordFocused(false);
                        }}
                        variant="website"
                        value={values.name}
                      />
                      <Typography className={styles.errorText} fontSize={8}>
                        {errors.name && (touched.name || values.name)
                          ? errors.name
                          : null}
                      </Typography>
                    </div>
                    <div className={styles.formErrorField}>
                      <Field
                        as={Input}
                        name="phone"
                        label={LOGIN_PAGE_CONSTANTS.PHONE_NUMBER}
                        required
                        type="tel"
                        placeholder={
                          LOGIN_PAGE_CONSTANTS.PHONE_NUMBER_PLACEHOLDER
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          let value = e.target.value;
                          if (!value.startsWith("+91-")) {
                            value = "+91-";
                          }
                          const digits = value.slice(3).replace(/\D/g, "");
                          const limitedDigits = digits.slice(0, 10);
                          value = "+91-" + limitedDigits;
                          setFieldValue("phone", value);
                        }}
                        onBlur={handleBlur}
                        onFocus={() => {
                          setShowPasswordValidations(false);
                          setIsPasswordFocused(false);
                        }}
                        value={values.phone}
                        variant="website"
                      />
                      <Typography className={styles.errorText} fontSize={8}>
                        {errors.phone && touched.phone && values.phone
                          ? errors.phone
                          : null}
                      </Typography>
                    </div>
                    <div className={styles.formErrorField}>
                      <Field
                        as={Input}
                        name="email"
                        label={LOGIN_PAGE_CONSTANTS.EMAIL_LABEL}
                        required
                        type="email"
                        disabled={isEdit}
                        placeholder={LOGIN_PAGE_CONSTANTS.EMAIL_PLACEHOLDER}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handleChange(e);
                          setEmailExistsError(null);
                        }}
                        onBlur={async (
                          e: React.FocusEvent<HTMLInputElement>,
                        ) => {
                          handleBlur(e);
                          setShowPasswordValidations(false);
                          setIsPasswordFocused(false);

                          const email = e.target.value;
                          const isValidEmail = Yup.string()
                            .email()
                            .required()
                            .isValidSync(email);

                          if (isValidEmail && !isEdit) {
                            try {
                              await triggerUserExistsQuery({
                                emailId: email,
                              }).unwrap();
                              setEmailExistsError(null);
                            } catch (err: any) {
                              if (err?.status === 409 && err?.data?.response) {
                                setEmailExistsError(err.data.response);
                              } else {
                                toast.error("Something went wrong");
                              }
                            }
                          }
                        }}
                        onFocus={() => {
                          setShowPasswordValidations(false);
                          setIsPasswordFocused(false);
                        }}
                        value={values.email}
                        variant="website"
                        autoComplete="off"
                      />
                      <Typography className={styles.errorText} fontSize={8}>
                        {emailExistsError ||
                          (errors.email && (touched.email || values.email)
                            ? errors.email
                            : null)}
                      </Typography>
                    </div>
                    {!isEdit && (
                      <>
                        <div className={styles.formErrorField}>
                          <div className={styles.passwordFieldWrapper}>
                            <Field
                              as={Input}
                              id="password-input"
                              name="password"
                              label={LOGIN_PAGE_CONSTANTS.PASSWORD_LABEL}
                              required
                              type={showPassword ? "text" : "password"}
                              placeholder={
                                LOGIN_PAGE_CONSTANTS.PASSWORD_PLACEHOLDER
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                handleChange(e);
                                handlePasswordChange(e);
                                if (e.target.value.length > 0) {
                                  setShowPasswordValidations(true);
                                } else {
                                  setShowPasswordValidations(false);
                                }
                              }}
                              onFocus={() => {
                                if (values.password.length > 0) {
                                  setShowPasswordValidations(true);
                                }
                                setIsPasswordFocused(true);
                              }}
                              onBlur={(
                                e: React.FocusEvent<HTMLInputElement>,
                              ) => {
                                const isClickingEyeIcon =
                                  e.relatedTarget?.closest(
                                    `.${styles.passwordIcon}`,
                                  );
                                if (!isClickingEyeIcon) {
                                  setIsPasswordFocused(false);
                                  setShowPasswordValidations(false);
                                }
                                handleBlur(e);
                              }}
                              value={values.password}
                              variant="website"
                              autoComplete="off"
                            />
                            <span
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowPassword((prev) => !prev);
                              }}
                              className={styles.passwordIcon}
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <Icon
                                  name="view_access_icon"
                                  color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                                />
                              ) : (
                                <Icon
                                  name="hide_access_icon"
                                  color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                                />
                              )}
                            </span>
                          </div>

                          {(isPasswordFocused ||
                            (showPasswordValidations && values.password)) && (
                            <div className={styles.passwordValidation}>
                              <div className={styles.validationColumn}>
                                <Typography
                                  fontSize={12}
                                  fontWeight="regular"
                                  color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                                  className={styles.validationMessage}
                                >
                                  <span
                                    className={`${styles.icon} ${
                                      isValidLength
                                        ? styles.valid
                                        : styles.invalid
                                    }`}
                                  >
                                    {isValidLength ? "✓" : "✗"}
                                  </span>
                                  {PASSWORD_VALIDATION.CHARACTER_LIMIT}
                                </Typography>
                                <Typography
                                  fontSize={12}
                                  fontWeight="regular"
                                  color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                                  className={styles.validationMessage}
                                >
                                  <span
                                    className={`${styles.icon} ${
                                      hasUppercase
                                        ? styles.valid
                                        : styles.invalid
                                    }`}
                                  >
                                    {hasUppercase ? "✓" : "✗"}
                                  </span>
                                  {PASSWORD_VALIDATION.UPPERCASE}
                                </Typography>
                                <Typography
                                  fontSize={12}
                                  fontWeight="regular"
                                  color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                                  className={styles.validationMessage}
                                >
                                  <span
                                    className={`${styles.icon} ${
                                      hasSpecialChar
                                        ? styles.valid
                                        : styles.invalid
                                    }`}
                                  >
                                    {hasSpecialChar ? "✓" : "✗"}
                                  </span>
                                  {PASSWORD_VALIDATION.SPECIAL_CHARACTER}
                                </Typography>
                              </div>
                              <div className={styles.validationColumn}>
                                <Typography
                                  fontSize={12}
                                  fontWeight="regular"
                                  color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                                  className={styles.validationMessage}
                                >
                                  <span
                                    className={`${styles.icon} ${
                                      hasLowercase
                                        ? styles.valid
                                        : styles.invalid
                                    }`}
                                  >
                                    {hasLowercase ? "✓" : "✗"}
                                  </span>
                                  {PASSWORD_VALIDATION.LOWER_CASE}
                                </Typography>
                                <Typography
                                  fontSize={12}
                                  fontWeight="regular"
                                  color={ICON_COLOR_CONSTANTS.TEXT_COLOR}
                                  className={styles.validationMessage}
                                >
                                  <span
                                    className={`${styles.icon} ${
                                      hasNumber ? styles.valid : styles.invalid
                                    }`}
                                  >
                                    {hasNumber ? "✓" : "✗"}
                                  </span>
                                  {PASSWORD_VALIDATION.NUMBER}
                                </Typography>
                              </div>
                            </div>
                          )}
                          <Typography className={styles.errorText} fontSize={8}>
                            {errors.password &&
                            (touched.password || values.password)
                              ? errors.password
                              : null}
                          </Typography>
                        </div>
                        <div className={styles.formErrorField}>
                          <div className={styles.passwordFieldWrapper}>
                            <Field
                              as={Input}
                              name="confirmPassword"
                              id="confirm-password-input"
                              label={
                                LOGIN_PAGE_CONSTANTS.CONFIRM_PASSWORD_LABEL
                              }
                              required
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder={
                                LOGIN_PAGE_CONSTANTS.CONFIRM_PASSWORD_PLACEHOLDER
                              }
                              onChange={handleChange}
                              onFocus={() => {
                                setShowPasswordValidations(false);
                                setIsPasswordFocused(false);
                              }}
                              onBlur={handleBlur}
                              value={values.confirmPassword}
                              variant="website"
                            />
                            <span
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowConfirmPassword((prev) => !prev);
                              }}
                              className={styles.passwordIcon}
                            >
                              {showConfirmPassword ? (
                                <Icon
                                  name="view_access_icon"
                                  color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                                />
                              ) : (
                                <Icon
                                  name="hide_access_icon"
                                  color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                                />
                              )}
                            </span>
                          </div>
                          <Typography className={styles.errorText} fontSize={8}>
                            {errors.confirmPassword &&
                            (touched.confirmPassword || values.confirmPassword)
                              ? errors.confirmPassword
                              : null}
                          </Typography>
                        </div>
                      </>
                    )}
                    {isEdit && (
                      <>
                        <div className={styles.formErrorField}>
                          <Field
                            as={Input}
                            name="superSetId"
                            label={"SuperSet Id"}
                            required={false}
                            type="text"
                            placeholder={LOGIN_PAGE_CONSTANTS.NAME_PLACEHOLDER}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={() => {
                              setShowPasswordValidations(false);
                              setIsPasswordFocused(false);
                            }}
                            variant="website"
                            value={values.superSetId}
                          />
                          <Typography className={styles.errorText} fontSize={8}>
                            {errors.superSetId &&
                            (touched.superSetId || values.superSetId)
                              ? errors.superSetId
                              : null}
                          </Typography>
                        </div>
                      </>
                    )}
                    <div className={styles.formErrorField}>
                      <label className={styles.genderLabel}>
                        <Typography
                          fontSize={16}
                          fontWeight="regular"
                          color="#808080"
                        >
                          <span style={{ color: "#e42525" }}>*</span> Gender
                        </Typography>
                      </label>
                      <div role="group" className={styles.genderOptions}>
                        <label className={styles.radioOption}>
                          <Field
                            type="radio"
                            name="gender"
                            value={GenderEnum.MALE}
                          />
                          <span className={styles.customRadio}></span>
                          <Typography
                            fontSize={16}
                            color="var(--website-text-color4)"
                          >
                            Male
                          </Typography>
                        </label>
                        <label className={styles.radioOption}>
                          <Field
                            type="radio"
                            name="gender"
                            value={GenderEnum.FEMALE}
                          />
                          <span className={styles.customRadio}></span>
                          <Typography
                            fontSize={16}
                            color="var(--website-text-color4)"
                          >
                            Female
                          </Typography>
                        </label>
                        <label className={styles.radioOption}>
                          <Field
                            type="radio"
                            name="gender"
                            value={GenderEnum.OTHERS}
                          />
                          <span className={styles.customRadio}></span>
                          <Typography
                            fontSize={16}
                            color="var(--website-text-color4)"
                          >
                            Other
                          </Typography>
                        </label>
                      </div>
                      <Typography className={styles.errorText} fontSize={8}>
                        {errors.gender && touched.gender ? errors.gender : null}
                      </Typography>
                    </div>
                  </div>
                  <div className={styles.loginBtn}>
                    <GradiantButton
                      variant="primary"
                      type="submit"
                      label={
                        isEdit ? "Update" : LOGIN_PAGE_CONSTANTS.REGISTER_BUTTON
                      }
                      width="400px"
                      height="40px"
                      fontSize={16}
                      labelClassName={styles.buttonLabel}
                      disabled={!(isValid && dirty) || !!emailExistsError}
                    />
                  </div>
                </Form>
              )}
            </Formik>
            {isLoading && (
              <CommonLoader
                color={COMMON_COLORS.WEBSITE_GREEN_LIGHT}
                overLayColor={COMMON_COLORS.LOADER_OVERLAY}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterAccount;
